import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  TextField,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];

export default function AddressAutocomplete({
  customers,
  values,
  handleNestedChange,
  handleAddressChange,
  setFieldValue,
  fieldName,
  error,
  helperText,
  onBlur,
}) {
  const [inputValue, setInputValue] = useState(
    values?.[fieldName]?.quickSearch || "",
  );
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const autocompleteService = useRef(null);
  const [loading, setLoading] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_API_MAP_KEY,
    libraries,
  });

  // Check if Google Maps API is loaded and initialize autocomplete service
  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      console.log("Google Maps initialized for", fieldName);
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      setIsGoogleMapsLoaded(true);
    } else {
      setIsGoogleMapsLoaded(false);
    }
  }, [isLoaded, fieldName]);

  // Update input value when quickSearch changes externally
  useEffect(() => {
    const quickSearch = values?.[fieldName]?.quickSearch || "";
    if (quickSearch !== inputValue) {
      setInputValue(quickSearch);
    }
  }, [values?.[fieldName]?.quickSearch]);

  // Filter customer addresses based on search input
  const filteredCustomers = useMemo(() => {
    if (!inputValue || inputValue.trim() === "") {
      return customers || [];
    }

    const searchTerm = inputValue.toLowerCase().trim();

    return (customers || []).filter((customer) => {
      const name = customer?.name?.toLowerCase() || "";
      const company = customer?.company?.toLowerCase() || "";
      const shortCode = customer?.shortCode?.toLowerCase() || "";
      const city = (
        customer?.address?.city ||
        customer?.addresses?.city ||
        ""
      ).toLowerCase();
      const state = (
        customer?.address?.state ||
        customer?.addresses?.state ||
        ""
      ).toLowerCase();
      const address1 = (
        customer?.address?.address ||
        customer?.addresses?.address ||
        customer?.address?.address1 ||
        customer?.addresses?.address1 ||
        ""
      ).toLowerCase();

      return (
        name.includes(searchTerm) ||
        company.includes(searchTerm) ||
        shortCode.includes(searchTerm) ||
        city.includes(searchTerm) ||
        state.includes(searchTerm) ||
        address1.includes(searchTerm)
      );
    });
  }, [customers, inputValue]);

  // Fetch Google Places suggestions when input changes
  useEffect(() => {
    if (
      !inputValue ||
      inputValue.trim() === "" ||
      !isGoogleMapsLoaded ||
      !autocompleteService.current
    ) {
      setGoogleSuggestions([]);
      return;
    }

    const fetchSuggestions = () => {
      autocompleteService.current.getPlacePredictions(
        {
          input: inputValue,
          componentRestrictions: { country: ["us", "ca"] },
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setGoogleSuggestions(predictions);
          } else {
            setGoogleSuggestions([]);
          }
        },
      );
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue, isGoogleMapsLoaded]);

  const handleSelect = async (place) => {
    setLoading(true);
    try {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ placeId: place.place_id }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const result = results[0];
          const addressComponents = result.address_components || [];
          let street = "";
          let city = "";
          let state = "";
          let postal = "";
          let country = "";

          addressComponents.forEach((component) => {
            const types = component.types;
            if (types.includes("street_number")) {
              street = component.long_name + " " + street;
            }
            if (types.includes("route")) {
              street = street + component.long_name;
            }
            if (types.includes("locality")) {
              city = component.long_name;
            }
            if (types.includes("administrative_area_level_1")) {
              state = component.short_name;
            }
            if (types.includes("postal_code")) {
              postal = component.long_name;
            }
            if (types.includes("country")) {
              country = component.long_name;
            }
          });

          // Update form values
          const name = place.description?.split(",")[0].trim();

          // Only update name and quickSearch for the current field
          handleNestedChange(fieldName, "name", name);
          handleNestedChange(fieldName, "quickSearch", place.description);

          // Only update shortCode if it's not a consignee
          if (fieldName === "shipper") {
            const shortCode = place.description
              .replace(/[^A-Z0-9]/gi, "")
              .substring(0, 5)
              .toUpperCase();
            handleNestedChange(fieldName, "shortCode", shortCode);
          }

          // Update address fields for the current field only
          const addressFields = {
            address: street,
            city,
            state,
            postal,
            country,
            latitude: result.geometry.location.lat(),
            longitude: result.geometry.location.lng(),
          };

          // Update the address object for the current field based on the field name
          if (fieldName === "shipper") {
            // For shipper, update the addresses directly
            setFieldValue("shipper.addresses", addressFields);
            Object.entries(addressFields).forEach(([key, value]) => {
              handleAddressChange(fieldName, key, value);
            });
          } else {
            // For other fields like consignee, update the address in orderStops
            Object.entries(addressFields).forEach(([key, value]) => {
              handleAddressChange(fieldName, key, value);
            });
          }
        }
        setLoading(false);
      });
    } catch (error) {
      console.error("Error: ", error);
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      fullWidth
      freeSolo
      options={[...filteredCustomers, ...googleSuggestions]}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
          handleNestedChange(fieldName, "quickSearch", newInputValue);
        }
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }
        if (option.description) {
          return option.description;
        }
        return option.company || option.name || "";
      }}
      filterOptions={(options) => options}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search by name or address"
          variant="outlined"
          fullWidth
          error={error}
          helperText={helperText?.trim() || null}
          onBlur={onBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading && (
                  <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                )}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        if (option.description) {
          return (
            <li {...props}>
              <Box sx={{ p: 0.5 }}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ fontWeight: 500 }}
                >
                  {option.description}
                </Typography>
              </Box>
            </li>
          );
        } else {
          return (
            <li {...props}>
              <Box>
                <Typography variant="body1">{option?.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option?.company || option?.shortCode} -{" "}
                  {option?.address?.city || option?.addresses?.city},{" "}
                  {option?.address?.state || option?.addresses?.state}
                </Typography>
              </Box>
            </li>
          );
        }
      }}
      groupBy={(option) => {
        return option?.description ? "Google Results" : "Address Book";
      }}
      onChange={(event, newValue) => {
        if (typeof newValue === "string") {
          setInputValue(newValue);
          handleNestedChange(fieldName, "quickSearch", newValue);
        } else if (newValue && newValue.description) {
          handleSelect(newValue);
        } else if (newValue) {
          handleNestedChange(fieldName, "quickSearch", newValue?.name);
          handleNestedChange(fieldName, "name", newValue?.name);
          handleNestedChange(fieldName, "shortCode", newValue?.shortCode || "");

          if (newValue.addresses || newValue.address) {
            handleNestedChange(
              fieldName,
              "name",
              newValue?.name || newValue?.company || "",
            );

            const addressValue =
              newValue.addresses?.address1 ||
              newValue.address?.address ||
              newValue.address?.address1 ||
              "";
            const cityValue =
              newValue.addresses?.city || newValue.address?.city || "";
            const stateValue =
              newValue.addresses?.state || newValue.address?.state || "";
            const postalValue =
              newValue.addresses?.postal || newValue.address?.postal || "";
            const countryValue =
              newValue.addresses?.country || newValue.address?.country || "";
            const latitudeValue =
              newValue.addresses?.latitude || newValue.address?.latitude || "";
            const longitudeValue =
              newValue.addresses?.longitude ||
              newValue.address?.longitude ||
              "";

            if (fieldName === "shipper") {
              setFieldValue("shipper.addresses", {
                address: addressValue,
                city: cityValue,
                state: stateValue,
                postal: postalValue,
                country: countryValue,
                latitude: latitudeValue,
                longitude: longitudeValue,
              });
            } else {
              handleAddressChange(fieldName, "address", addressValue);
              handleAddressChange(fieldName, "city", cityValue);
              handleAddressChange(fieldName, "state", stateValue);
              handleAddressChange(fieldName, "postal", postalValue);
              handleAddressChange(fieldName, "country", countryValue);
              handleAddressChange(fieldName, "latitude", latitudeValue);
              handleAddressChange(fieldName, "longitude", longitudeValue);
            }
          }

          handleNestedChange(
            fieldName,
            "contactName",
            newValue.contactName || "",
          );
          handleNestedChange(
            fieldName,
            "contactPhone",
            newValue.contactPhone || "",
          );
          handleNestedChange(
            fieldName,
            "contactEmail",
            newValue.contactEmail || "",
          );

          setInputValue(newValue?.name || "");
        }
      }}
    />
  );
}
